import { useEffect, useRef, useState } from "react";
import "./styles.css";

const tan15 = Math.tan(15 * (Math.PI / 180));
const firstEdgePoint = (size: number) => size / 2 - tan15 * (size / 2);
const secondEdgePoint = (size: number) => size / 2 + tan15 * (size / 2);

const whiteFaceColor = "#FFFA";
const orangeFaceColor = "darkorange";
const redFaceColor = "red";
const yellowFaceColor = "#FF0A";
const blueFaceColor = "blue";
const greenFaceColor = "green";

const sliverWidth = 2;
const kiteWidth = 4;

const axisHintSize = 1.2;

function chooseRandom(arr: any[]) {
  return arr.at(Math.floor(arr.length * Math.random()));
}

export default function App() {
  return (
    <main>
      <div className="center">
        <h1 className="page-title">Square-1</h1>
        <sub className="page-subtitle">
          <a href="https://en.wikipedia.org/wiki/Square-1_(puzzle)">
            What is Square-1?
          </a>
        </sub>
        <Cube />
      </div>
    </main>
  );
}

function Cube() {
  const [width, height] = [400, 400];
  const [topFace, setTopFace] = useState(getDefaultTopFace(width, height));
  const [bottomFace, setBottomFace] = useState(
    getDefaultBottomFace(width, height)
  );
  const [isMiddleSquare, setIsMiddleSquare] = useState(true);

  const [showTopFace, setShowTopFace] = useState(true);
  const [showBottomFace, setShowBottomFace] = useState(true);
  const [showDebug, setShowDebug] = useState(false);

  const [scrambleStepCount, setScrambleStepCount] = useState(0);
  const [scrambleLastAxis, setScrambleLastAxis] = useState(-1);
  const [scrambleSpeed, setScrambleSpeed] = useState(50);

  const [showSpecificFlips, setShowSpecificFlips] = useState<boolean[]>(
    Array(12).fill(false)
  );

  const svgRef = useRef<SVGElement>();

  // Actual scrambling part of the scrambling system
  useEffect(() => {
    if (scrambleStepCount > 0) {
      setTimeout(() => {
        scrambleStep();
        setScrambleStepCount((s) => s - 1);
      }, scrambleSpeed);
    }
  }, [scrambleStepCount]);

  // Applies the rotation to each individual piece on the face
  // Makes the state easier to manipulate than storing face rotation separately.
  function rotateFace(face: Piece[], deg: number) {
    const newFace = [];
    for (const piece of face)
      newFace.push({ ...piece, rotation: piece.rotation + deg });
    return newFace;
  }

  // Flip in the third dimension (toward the screen) half a face.
  // Equal to a cube 180deg rotation on a Square1
  function _flipOnAxis(axis: number, rotation: number) {
    // Flipping on an axis here would mean reflecting about the orthogonal axis
    rotation = normalizeDegrees(rotation);
    const orthogonal = (axis + 6) % 12;
    const diff = rotation - orthogonal * 15;
    return normalizeDegrees(orthogonal * 15 - diff);
  }

  // Puts deg in [0, 360)
  function normalizeDegrees(deg: number) {
    return (deg % 360) + (deg >= 0 ? 0 : 360);
  }

  // Rounds to nearest factor of 15, for position calculations.
  // The cube is structured around increments of 15 degrees.
  function nearest15(deg: number) {
    const remainder = deg % 15;
    if (remainder < 15 / 2) return deg - remainder;
    return deg + remainder;
  }

  /** Find if an angle is within a circular range. */
  function clockwiseRange(start: number, end: number, deg: number) {
    start = normalizeDegrees(start);
    end = normalizeDegrees(end);
    deg = normalizeDegrees(deg);
    if (end < start) end += 360;
    return start <= deg && deg <= end;
  }

  // Modifies all pieces on half of the cube to effectively
  // flip them on an axis defined by a plane/cut through the center.
  // All pieces on one side of the specified axis will be flipped:
  // Mirrored and on the opposite face.
  /* Axis: 15*axis degrees from top middle, clockwise. */
  function rotateAboutAxis(axis: number) {
    const aStart = axis * 15;
    const aEnd = axis * 15 + 180;
    const toFlipTop: Piece[] = [];
    const toFlipBottom: Piece[] = [];
    for (let i = 0; i < topFace.length; i++) {
      const topPiece = topFace[i];
      const topPieceWidth = topPiece.isSliver ? sliverWidth : kiteWidth;
      if (clockwiseRange(aStart, aEnd, topPiece.rotation - topPieceWidth / 2)) {
        toFlipTop.push(topPiece);
      }
    }

    for (let i = 0; i < bottomFace.length; i++) {
      const bottomPiece = bottomFace[i];
      const bottomPieceWidth = bottomPiece.isSliver ? sliverWidth : kiteWidth;
      if (
        clockwiseRange(
          aStart,
          aEnd,
          bottomPiece.rotation - bottomPieceWidth / 2
        )
      ) {
        toFlipBottom.push(bottomPiece);
      }
    }

    setTopFace((oldFace) => {
      const newFace: Piece[] = [];
      for (let i = 0; i < oldFace.length; i++) {
        if (toFlipTop.findIndex((piece) => piece == oldFace[i]) < 0) {
          newFace.push(oldFace[i]);
        }
      }

      for (const piece of toFlipBottom) {
        newFace.push({
          ...piece,
          rotation: _flipOnAxis(axis, piece.rotation),
          edge1Color: piece.isSliver ? piece.edge1Color : piece.edge2Color,
          edge2Color: piece.isSliver ? piece.edge2Color : piece.edge1Color,
        });
      }

      return newFace;
    });

    setBottomFace((oldFace) => {
      const newFace: Piece[] = [];
      for (let i = 0; i < oldFace.length; i++) {
        if (toFlipBottom.findIndex((piece) => piece == oldFace[i]) < 0) {
          newFace.push(oldFace[i]);
        }
      }

      for (const piece of toFlipTop) {
        newFace.push({
          ...piece,
          rotation: _flipOnAxis(axis, piece.rotation),
          edge1Color: piece.isSliver ? piece.edge1Color : piece.edge2Color,
          edge2Color: piece.isSliver ? piece.edge2Color : piece.edge1Color,
        });
      }

      return newFace;
    });
    toggleMiddle();
  }

  // Used to identify the
  /* Degrees: 15-deg increments around circle. 24 total.
     Axes, 15-deg increment lines through circle, 12 total (2 opposite degrees make 1 axis) */
  function _getFreeFaceDegsAndAxes(face: Piece[]) {
    const isDegFree: boolean[] = Array(24).fill(false);
    const isAxisFree: boolean[] = Array(12).fill(false);
    for (const piece of face) {
      let rot = normalizeDegrees(piece.rotation);
      const pieceWidth = piece.isSliver ? sliverWidth : kiteWidth;
      const leftIndex = rot / 15 - pieceWidth / 2;
      const rightIndex = rot / 15 + pieceWidth / 2;
      isDegFree[leftIndex % isDegFree.length] = true;
      isDegFree[rightIndex % isDegFree.length] = true;

      if (isDegFree[(leftIndex + 12) % isDegFree.length])
        isAxisFree[leftIndex % isAxisFree.length] = true;
      if (isDegFree[(rightIndex + 12) % isDegFree.length])
        isAxisFree[rightIndex % isAxisFree.length] = true;
    }
    return [isDegFree, isAxisFree];
  }

  function getFreeFaceDegs(face: Piece[]) {
    return _getFreeFaceDegsAndAxes(face)[0];
  }

  function getFreeFaceAxes(face: Piece[]) {
    return _getFreeFaceDegsAndAxes(face)[1];
  }

  function getFreeCubeAxes() {
    const topFaceAxes = getFreeFaceAxes(topFace);
    const bottomFaceAxes = getFreeFaceAxes(bottomFace);
    const fullyClearAxes = [];
    for (let i = 0; i < topFaceAxes.length; i++) {
      fullyClearAxes.push(topFaceAxes[i] == true && bottomFaceAxes[i] == true);
    }
    return fullyClearAxes;
  }

  function setSpecificFlipDisplay(flip: number, value: boolean) {
    setShowSpecificFlips((f) => {
      const newF = [...f];
      newF[flip] = value;
      return newF;
    });
  }

  function generateFlipButtons() {
    const freeAxes = getFreeCubeAxes();
    const buttons = Array(12)
      .fill(true)
      .map((b, i) => (
        <button
          onClick={() => buttonFlip(i)}
          key={i}
          onMouseEnter={() => setSpecificFlipDisplay(i, true)}
          onMouseLeave={() => setSpecificFlipDisplay(i, false)}
          disabled={!freeAxes[i]}
        >
          {i}
        </button>
      ));
    return buttons;
  }

  function toggleMiddle() {
    setIsMiddleSquare((a) => !a);
  }

  function buttonRotateTopLeft() {
    setTopFace((face) => rotateFace(face, -15));
  }

  function buttonRotateTopRight() {
    setTopFace((face) => rotateFace(face, 15));
  }

  function buttonRotateBottomLeft() {
    setBottomFace((face) => rotateFace(face, -15));
  }

  function buttonRotateBottomRight() {
    setBottomFace((face) => rotateFace(face, 15));
  }

  function buttonFlip(axis: number) {
    if (canFlipOnAxis(axis)) rotateAboutAxis(axis);
  }

  function canFlipOnAxis(axis: number) {
    const freeAxes = getFreeCubeAxes();
    return freeAxes[axis];
  }

  function getFreeAxesIndices() {
    const freeAxes = getFreeCubeAxes();
    return freeAxes.flatMap((a, i) => (a ? [i] : []));
  }

  function scramble(steps: number, speed: number) {
    if (scrambleStepCount > 0) return;
    setScrambleSpeed(speed);
    setScrambleStepCount(steps);
  }

  function scrambleStep() {
    const flipProb = 0.5;
    const freeAxes = getFreeAxesIndices();

    if (freeAxes.length == 0 || Math.random() > flipProb) {
      const rotateButtons = [
        buttonRotateTopLeft,
        buttonRotateTopRight,
        buttonRotateBottomLeft,
        buttonRotateBottomRight,
      ];
      chooseRandom(rotateButtons)();
    } else {
      if (freeAxes.length > 0) {
        let axis = chooseRandom(freeAxes);
        if (scrambleLastAxis == axis) {
          axis = freeAxes.find((a) => a != axis);
        }
        if (axis !== undefined) {
          buttonFlip(axis);
          setScrambleLastAxis(axis);
        }
      }
    }
  }

  function resetCube() {
    setTopFace(getDefaultTopFace(width, height));
    setBottomFace(getDefaultBottomFace(width, height));
  }

  return (
    <div className="cube-container">
      <div className="buttons">
        <div className="visibility-buttons">
          <span className="button-label">Show: </span>
          <button
            onClick={() => setShowTopFace((x) => !x)}
            className={(showTopFace ? "" : "desat") + " top-button"}
          >
            Top
          </button>
          <button
            onClick={() => setShowBottomFace((x) => !x)}
            className={(showBottomFace ? "" : "desat") + " bottom-button"}
          >
            Bottom
          </button>
          {/* <button
            onClick={() => setShowDebug((x) => !x)}
            className={showDebug ? "" : "desat"}
          >
            Debug
          </button> */}
        </div>
        <div className="rotate-buttons top-rotate">
          <span className="button-label">Rotate top: </span>
          <button onClick={buttonRotateTopLeft} className="top-button">
            L
          </button>
          <button onClick={buttonRotateTopRight} className="top-button">
            R
          </button>
        </div>
        <div className="rotate-buttons bottom-rotate">
          <span className="button-label">Rotate bottom: </span>
          <button onClick={buttonRotateBottomLeft} className="bottom-button">
            L
          </button>
          <button onClick={buttonRotateBottomRight} className="bottom-button">
            R
          </button>
        </div>
        <div className="flip-buttons">
          <span className="button-label">Flip: </span>
          {generateFlipButtons()}
        </div>
        <div className="rotate-buttons">
          <span className="button-label">Scramble: </span>
          <button onClick={() => scramble(30, 80)}>A little</button>
          <button onClick={() => scramble(200, 20)}>A lot</button>
        </div>
        <div className="rotate-buttons">
          <span className="button-label">Reset: </span>
          <button onClick={() => resetCube()}>Reset cube</button>
        </div>
      </div>

      <div
        className="cube-outer"
        style={{ width: Math.sqrt(2) * width, height: Math.sqrt(2) * height }}
      >
        <div
          className="cube-svg-container"
          style={{ left: -width / 2, top: ((Math.sqrt(2) - 1) / 2) * height }}
        >
          {showBottomFace &&
            bottomFace.map((piece: Piece, i) => {
              if (piece.isSliver)
                return (
                  <SliverPiece {...piece} rotation={piece.rotation} key={i} />
                );
              else
                return (
                  <KitePiece {...piece} rotation={piece.rotation} key={i} />
                );
            })}
          {showTopFace &&
            topFace.map((piece: Piece, i) => {
              if (piece.isSliver)
                return (
                  <SliverPiece {...piece} rotation={piece.rotation} key={i} />
                );
              else
                return (
                  <KitePiece {...piece} rotation={piece.rotation} key={i} />
                );
            })}
          {showDebug && (
            <>
              <svg width={width} height={height}>
                {getFreeFaceDegs(topFace).map((p, i) => (
                  <circle
                    cx={
                      Math.sin(-(i * Math.PI) / 12 + Math.PI) * (width / 2.5) +
                      width / 2
                    }
                    cy={
                      Math.cos((i * Math.PI) / 12 + Math.PI) * (width / 2.5) +
                      width / 2
                    }
                    r={10}
                    style={{
                      fill: p ? "#00F" : "#F335",
                    }}
                    key={i}
                  />
                ))}
              </svg>
              <svg width={width} height={height}>
                {getFreeCubeAxes().map((p, i) => (
                  <path
                    d={`M${
                      Math.sin(-(i * Math.PI) / 12 + Math.PI) * (width / 2.5) +
                      width / 2
                    } ${
                      Math.cos((i * Math.PI) / 12 + Math.PI) * (width / 2.5) +
                      width / 2
                    } L${
                      Math.sin(-((i + 12) * Math.PI) / 12 + Math.PI) *
                        (width / 2.5) +
                      width / 2
                    } ${
                      Math.cos(((i + 12) * Math.PI) / 12 + Math.PI) *
                        (width / 2.5) +
                      width / 2
                    }`}
                    style={{ stroke: p ? "lime" : "#0000", strokeWidth: 2 }}
                    key={i}
                  />
                ))}
              </svg>
            </>
          )}
          <svg
            width={width * axisHintSize}
            height={height * axisHintSize}
            style={{
              top: -(width * ((axisHintSize - 1) / 2)) + "px",
              left: (width / 2.33) * Math.sqrt(2) + "px",
            }}
          >
            {getFreeCubeAxes().map((p, i) =>
              showSpecificFlips[i] ? (
                <path
                  d={`M${
                    Math.sin(-(i * Math.PI) / 12 + Math.PI) * width +
                    (width * axisHintSize) / 2
                  } ${
                    Math.cos((i * Math.PI) / 12 + Math.PI) * width +
                    (width * axisHintSize) / 2
                  } L${
                    Math.sin(-((i + 12) * Math.PI) / 12 + Math.PI) * width +
                    (width * axisHintSize) / 2
                  } ${
                    Math.cos(((i + 12) * Math.PI) / 12 + Math.PI) * width +
                    (width * axisHintSize) / 2
                  }`}
                  style={{
                    stroke: p ? "lime" : "#0000",
                    strokeWidth: 3,
                    strokeDasharray: "5, 5",
                  }}
                  key={i}
                />
              ) : (
                <></>
              )
            )}
          </svg>
        </div>
      </div>
    </div>
  );
}

const getDefaultTopFace = (width: number, height: number): Piece[] => {
  const dfault = {
    width,
    height,
    topColor: yellowFaceColor,
    flip: false,
  };
  const result: Piece[] = [];
  result.push({
    ...dfault,
    isSliver: true,
    rotation: 0,
    edge1Color: blueFaceColor,
    edge2Color: "",
  });
  result.push({
    ...dfault,
    isSliver: false,
    rotation: 45,
    edge1Color: blueFaceColor,
    edge2Color: orangeFaceColor,
  });
  result.push({
    ...dfault,
    isSliver: true,
    rotation: 90,
    edge1Color: orangeFaceColor,
    edge2Color: "",
  });
  result.push({
    ...dfault,
    isSliver: false,
    rotation: 135,
    edge1Color: orangeFaceColor,
    edge2Color: greenFaceColor,
  });
  result.push({
    ...dfault,
    isSliver: true,
    rotation: 180,
    edge1Color: greenFaceColor,
    edge2Color: "",
  });
  result.push({
    ...dfault,
    isSliver: false,
    rotation: 225,
    edge1Color: greenFaceColor,
    edge2Color: redFaceColor,
  });
  result.push({
    ...dfault,
    isSliver: true,
    rotation: 270,
    edge1Color: redFaceColor,
    edge2Color: "",
  });
  result.push({
    ...dfault,
    isSliver: false,
    rotation: 315,
    edge1Color: redFaceColor,
    edge2Color: blueFaceColor,
  });

  return result;
};

const getDefaultBottomFace = (width: number, height: number): Piece[] => {
  const dfault = {
    width,
    height,
    topColor: whiteFaceColor,
    flip: false,
  };
  const result: Piece[] = [];
  result.push({
    ...dfault,
    isSliver: true,
    rotation: 0,
    edge1Color: blueFaceColor,
    edge2Color: "",
  });
  result.push({
    ...dfault,
    isSliver: false,
    rotation: 45,
    edge1Color: blueFaceColor,
    edge2Color: orangeFaceColor,
  });
  result.push({
    ...dfault,
    isSliver: true,
    rotation: 90,
    edge1Color: orangeFaceColor,
    edge2Color: "",
  });
  result.push({
    ...dfault,
    isSliver: false,
    rotation: 135,
    edge1Color: orangeFaceColor,
    edge2Color: greenFaceColor,
  });
  result.push({
    ...dfault,
    isSliver: true,
    rotation: 180,
    edge1Color: greenFaceColor,
    edge2Color: "",
  });
  result.push({
    ...dfault,
    isSliver: false,
    rotation: 225,
    edge1Color: greenFaceColor,
    edge2Color: redFaceColor,
  });
  result.push({
    ...dfault,
    isSliver: true,
    rotation: 270,
    edge1Color: redFaceColor,
    edge2Color: "",
  });
  result.push({
    ...dfault,
    isSliver: false,
    rotation: 315,
    edge1Color: redFaceColor,
    edge2Color: blueFaceColor,
  });

  return result;
};

type Piece = {
  topColor: string;
  rotation: number;
  flip: boolean;
  width: number;
  height: number;
  edge1Color: string;
  edge2Color: string;
  isSliver: boolean;
};

function SliverPiece({
  topColor,
  edge1Color,
  rotation,
  width,
  height,
}: {
  topColor: string;
  edge1Color: string;
  rotation: number;
  width: number;
  height: number;
}) {
  return (
    <svg
      width={width}
      height={height}
      style={{
        rotate: rotation + "deg",
      }}
    >
      <path
        d={`M${width / 2} ${height / 2} L ${firstEdgePoint(
          width
        )} ${0} L ${secondEdgePoint(width)} ${0} Z`}
        style={{
          fill: topColor,
          stroke: "black",
          strokeWidth: 5,
        }}
        strokeLinejoin="round"
      />
      <path
        d={`M${firstEdgePoint(width)} ${0} L ${secondEdgePoint(width)} ${0}`}
        style={{
          fill: "none",
          stroke: edge1Color,
          strokeWidth: 15,
        }}
        strokeLinejoin="bevel"
      />
    </svg>
  );
}

function KitePiece({
  topColor,
  edge1Color,
  edge2Color,
  rotation,
  flip,
  width,
  height,
}: {
  topColor: string;
  edge1Color: string;
  edge2Color: string;
  rotation: number;
  flip: boolean;
  width: number;
  height: number;
}) {
  return (
    <svg
      width={width}
      height={height}
      style={{
        rotate: rotation - 45 + "deg",
      }}
    >
      <path
        d={`M${width / 2} ${height / 2} L ${secondEdgePoint(
          width
        )} ${0} L ${width} ${0}  L ${width} ${firstEdgePoint(width)} Z`}
        style={{ fill: topColor, stroke: "black", strokeWidth: 5 }}
        strokeLinejoin="round"
      />

      <path
        d={`M${secondEdgePoint(width)} ${0} L ${width} ${0}`}
        style={{
          fill: "none",
          stroke: flip ? edge2Color : edge1Color,
          strokeWidth: 15,
        }}
        strokeLinejoin="bevel"
      />

      <path
        d={`M${width} ${0} L ${width} ${firstEdgePoint(width)}`}
        style={{
          fill: "none",
          stroke: flip ? edge1Color : edge2Color,
          strokeWidth: 15,
        }}
        strokeLinejoin="bevel"
      />
    </svg>
  );
}

/*
TODO: Better interface for flipping and rotating.
TODO: Reconfigure the page layout for easier CSS
*/
